import { useCallback, useMemo, useRef, useState } from "react";
import { useAPI } from "@humansignal/core";
import { Button, Tooltip, useToast } from "@humansignal/ui";
import { IconFileDownload } from "@humansignal/icons";
import { Modal } from "../../../components/Modal/Modal";
import { cn } from "../../../utils/bem";
import { EMAIL_RE } from "../../../components/EmailChipInput/EmailChipInput";
import "./UploadMembersTab.prefix.css";

/**
 * Invite members from a file (CSV or JSON) with column mapping.
 *
 * 1. Drop / choose a file (.csv, .json) or paste CSV text.
 * 2. Map columns → Email (required), Name or First/Last (optional),
 *    Role (optional; values like "annotator" / "AN" / "Reviewer").
 * 3. Preview the parsed rows (invalid emails flagged), pick a default
 *    role for rows without one and optional projects.
 * 4. Invite — the server creates accounts where needed, sends the
 *    set-password email, and applies per-row roles/names.
 */

const block = cn("upload-members");

const ROLE_OPTIONS = [
  { value: "OW", label: "Owner" },
  { value: "AD", label: "Admin" },
  { value: "MA", label: "Manager" },
  { value: "RE", label: "Reviewer" },
  { value: "AN", label: "Annotator" },
  { value: "GU", label: "Guest" },
];
const ROLE_ALIASES = {
  ow: "OW",
  owner: "OW",
  ad: "AD",
  admin: "AD",
  administrator: "AD",
  ma: "MA",
  manager: "MA",
  re: "RE",
  reviewer: "RE",
  an: "AN",
  annotator: "AN",
  gu: "GU",
  guest: "GU",
};

/** Minimal RFC-4180-ish CSV parser: quotes, escaped quotes, CR/LF. */
export const parseCsv = (text) => {
  const rows = [];
  let row = [];
  let field = "";
  let inQ = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQ) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else inQ = false;
      } else field += c;
    } else if (c === '"') inQ = true;
    else if (c === "," || c === ";" || c === "\t") {
      row.push(field);
      field = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      row.push(field);
      field = "";
      if (row.some((x) => x.trim() !== "")) rows.push(row);
      row = [];
    } else field += c;
  }
  row.push(field);
  if (row.some((x) => x.trim() !== "")) rows.push(row);
  return rows;
};

/** Normalise any input (CSV text or JSON) into { columns, rows } of strings. */
export const toTable = (text, filename = "") => {
  const trimmed = text.trim();
  const looksJson = filename.toLowerCase().endsWith(".json") || trimmed.startsWith("[") || trimmed.startsWith("{");
  if (looksJson) {
    let data = JSON.parse(trimmed);
    if (!Array.isArray(data)) data = data.members ?? data.users ?? data.rows ?? [data];
    const columns = [];
    for (const r of data) for (const k of Object.keys(r || {})) if (!columns.includes(k)) columns.push(k);
    return { columns, rows: data.map((r) => columns.map((c) => (r?.[c] == null ? "" : String(r[c])))) };
  }
  const rows = parseCsv(trimmed);
  if (!rows.length) return { columns: [], rows: [] };
  const header = rows[0].map((h, i) => h.trim() || `column ${i + 1}`);
  // Heuristic: if no header cell looks like a name and the first row has an email, treat it as data with generic headers
  const headerHasEmailValue = rows[0].some((h) => EMAIL_RE.test(h.trim()));
  if (headerHasEmailValue) {
    return { columns: rows[0].map((_, i) => `column ${i + 1}`), rows };
  }
  return { columns: header, rows: rows.slice(1) };
};

const guessColumn = (columns, candidates) => {
  const lower = columns.map((c) => c.toLowerCase().replace(/[^a-z]/g, ""));
  for (const cand of candidates) {
    const i = lower.findIndex((c) => c === cand || c.includes(cand));
    if (i >= 0) return columns[i];
  }
  return "";
};

export const UploadMembersTab = ({ org, onAdded }) => {
  const api = useAPI();
  const toast = useToast();
  const fileRef = useRef(null);
  const [table, setTable] = useState(null); // { columns, rows }
  const [fileName, setFileName] = useState("");
  const [map, setMap] = useState({ email: "", name: "", first: "", last: "", role: "" });
  const [defaultRole, setDefaultRole] = useState(org?.default_role ?? "AN");
  const [parseError, setParseError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  // Nothing is saved until the reviewer confirms the full list here.
  const [reviewOpen, setReviewOpen] = useState(false);
  const [pasted, setPasted] = useState("");

  const load = useCallback((text, name = "") => {
    setResult(null);
    try {
      const t = toTable(text, name);
      if (!t.columns.length) throw new Error("No rows found");
      setTable(t);
      setFileName(name);
      setMap({
        email: guessColumn(t.columns, ["email", "mail", "e-mail", "address"]),
        name: guessColumn(t.columns, ["fullname", "name"]),
        first: guessColumn(t.columns, ["firstname", "first", "givenname", "given"]),
        last: guessColumn(t.columns, ["lastname", "last", "surname", "familyname"]),
        role: guessColumn(t.columns, ["role"]),
      });
      setParseError("");
    } catch (e) {
      setTable(null);
      setParseError(`Could not read the file: ${e.message}`);
    }
  }, []);

  const onFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => load(String(reader.result || ""), file.name);
    reader.readAsText(file);
  };

  const idx = (col) => (table ? table.columns.indexOf(col) : -1);
  const members = useMemo(() => {
    if (!table || !map.email) return [];
    const ei = idx(map.email);
    const ni = idx(map.name);
    const fi = idx(map.first);
    const li = idx(map.last);
    const ri = idx(map.role);
    const seen = new Set();
    return table.rows.map((r) => {
      const email = (r[ei] || "").trim().toLowerCase();
      const rawRole = ri >= 0 ? (r[ri] || "").trim().toLowerCase() : "";
      const role = rawRole ? ROLE_ALIASES[rawRole] : "";
      const m = {
        email,
        first_name: fi >= 0 ? (r[fi] || "").trim() : "",
        last_name: li >= 0 ? (r[li] || "").trim() : "",
        name: ni >= 0 ? (r[ni] || "").trim() : "",
        role: role || "",
        rawRole,
        valid: EMAIL_RE.test(email),
        dup: false,
        badRole: !!rawRole && !role,
      };
      if (m.valid) {
        if (seen.has(email)) m.dup = true;
        seen.add(email);
      }
      return m;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, map]);
  const ready = members.filter((m) => m.valid && !m.dup);
  const invalid = members.filter((m) => !m.valid);
  const dups = members.filter((m) => m.dup);
  const badRoles = members.filter((m) => m.badRole);

  const submit = useCallback(async () => {
    if (!ready.length) return;
    setSubmitting(true);
    const res = await api.callApi("orgInviteByEmail", {
      params: { pk: org.id },
      body: {
        role: defaultRole,
        members: ready.map((m) => ({
          email: m.email,
          role: m.role || undefined,
          first_name: m.first_name || undefined,
          last_name: m.last_name || undefined,
          name: !m.first_name && !m.last_name ? m.name || undefined : undefined,
        })),
      },
      errorFilter: () => true,
    });
    setSubmitting(false);
    setReviewOpen(false);
    if (res && !res.error) {
      setResult(res);
      onAdded?.();
      const added = res.added?.length ?? 0;
      const created = res.created?.length ?? 0;
      toast.show({
        message: `Invited ${added} member${added !== 1 ? "s" : ""}${created ? ` (${created} new account${created !== 1 ? "s" : ""} created)` : ""}.`,
      });
    } else {
      toast.show({ message: res?.error ?? res?.detail ?? "Failed to invite members", type: "error" });
    }
  }, [api, org, ready, defaultRole, toast, onAdded]);

  const reset = () => {
    setTable(null);
    setFileName("");
    setResult(null);
    setParseError("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const template = "email,name,role\namina@example.org,Amina Bello,annotator\nkofi@example.org,Kofi Mensah,reviewer\n";
  const templateHref = `data:text/csv;charset=utf-8,${encodeURIComponent(template)}`;

  return (
    <div className={block.toClassName()}>
      <p className={block.elem("description").toClassName()}>
        CSV or JSON with an <b>email</b> column; <b>name</b> and <b>role</b> (annotator, reviewer, manager, admin,
        owner, guest) are optional.
      </p>

      {!table && (
        <div
          className={block.elem("drop").toClassName()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            onFile(e.dataTransfer.files?.[0]);
          }}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.json,text/csv,application/json,text/plain"
            hidden
            onChange={(e) => onFile(e.target.files?.[0])}
          />
          <div className={block.elem("drop-row").toClassName()}>
            <span className={block.elem("drop-text").toClassName()}>Drop a .csv or .json file here, or</span>
            <div className={block.elem("drop-actions").toClassName()}>
              <Button size="small" onClick={() => fileRef.current?.click()}>
                Choose file
              </Button>
              <Tooltip title="Download a CSV template (email, name, role)">
                <a
                  className={block.elem("icon-link").toClassName()}
                  href={templateHref}
                  download="members-template.csv"
                  aria-label="Download a CSV template"
                >
                  <IconFileDownload />
                </a>
              </Tooltip>
            </div>
          </div>
          {parseError && <div className={block.elem("error").toClassName()}>{parseError}</div>}
        </div>
      )}
      {!table && (
        <div className={block.elem("paste-card").toClassName()}>
          <span className={block.elem("drop-text").toClassName()}>Or paste CSV / JSON</span>
          <textarea
            className={block.elem("paste").toClassName()}
            placeholder={"email,name,role\nabc@example.org,Abc Def,annotator"}
            rows={4}
            value={pasted}
            onChange={(e) => setPasted(e.target.value)}
          />
          <div className={block.elem("paste-actions").toClassName()}>
            <Button size="small" look="outlined" disabled={!pasted.trim()} onClick={() => load(pasted, "pasted text")}>
              Use this text
            </Button>
          </div>
        </div>
      )}

      {table && (
        <>
          <div className={block.elem("file-row").toClassName()}>
            <span className={block.elem("file-name").toClassName()}>{fileName || "pasted text"}</span>
            <span className={block.elem("muted").toClassName()}>
              {table.rows.length} row{table.rows.length !== 1 ? "s" : ""}, {table.columns.length} column
              {table.columns.length !== 1 ? "s" : ""}
            </span>
            <Button size="small" look="outlined" onClick={reset}>
              Use another file
            </Button>
          </div>

          <div className={block.elem("mapping").toClassName()}>
            {[
              ["email", "Email (required)"],
              ["name", "Full name"],
              ["first", "First name"],
              ["last", "Last name"],
              ["role", "Role"],
            ].map(([key, label]) => (
              <label key={key} className={block.elem("map-field").toClassName()}>
                <span>{label}</span>
                <select
                  className={block.elem("select").toClassName()}
                  value={map[key]}
                  onChange={(e) => setMap((m) => ({ ...m, [key]: e.target.value }))}
                >
                  <option value="">{key === "email" ? "— choose a column —" : "— none —"}</option>
                  {table.columns.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>
            ))}
            <label className={block.elem("map-field").toClassName()}>
              <span>Role when the row has none</span>
              <select
                className={block.elem("select").toClassName()}
                value={defaultRole}
                onChange={(e) => setDefaultRole(e.target.value)}
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {map.email ? (
            <div className={block.elem("preview").toClassName()}>
              <div className={block.elem("summary").toClassName()}>
                <span>
                  <b>{ready.length}</b> to invite
                </span>
                {invalid.length > 0 && (
                  <span className={block.elem("warn").toClassName()}>
                    {invalid.length} invalid email{invalid.length !== 1 ? "s" : ""} (skipped)
                  </span>
                )}
                {dups.length > 0 && (
                  <span className={block.elem("warn").toClassName()}>
                    {dups.length} duplicate{dups.length !== 1 ? "s" : ""} (skipped)
                  </span>
                )}
                {badRoles.length > 0 && (
                  <span className={block.elem("warn").toClassName()}>
                    {badRoles.length} unknown role value{badRoles.length !== 1 ? "s" : ""} (default role used)
                  </span>
                )}
              </div>
              <div className={block.elem("muted").toClassName()}>
                Nothing is sent yet — review the full list in the next step.
              </div>
            </div>
          ) : (
            <div className={block.elem("muted").toClassName()}>Choose which column holds the email address.</div>
          )}

          {result && (
            <div className={block.elem("result").toClassName()}>
              {result.added?.length > 0 && <div>✓ Invited: {result.added.length}</div>}
              {result.created?.length > 0 && (
                <div>✓ New accounts created (set-password email sent): {result.created.length}</div>
              )}
              {result.already_members?.length > 0 && <div>Already members: {result.already_members.join(", ")}</div>}
              {result.errors?.length > 0 && (
                <div className={block.elem("error").toClassName()}>Errors: {result.errors.join(", ")}</div>
              )}
            </div>
          )}

          <div className={block.elem("actions").toClassName()}>
            <Button onClick={() => setReviewOpen(true)} size="small" disabled={!ready.length || submitting}>
              {`Review ${ready.length || ""} member${ready.length !== 1 ? "s" : ""}…`}
            </Button>
          </div>

          {reviewOpen && (
            <Modal
              visible
              onHide={() => setReviewOpen(false)}
              title={`Invite ${ready.length} member${ready.length !== 1 ? "s" : ""}?`}
              style={{ width: 640 }}
              footer={
                <div className={block.elem("modal-foot").toClassName()}>
                  <span className={block.elem("muted").toClassName()}>
                    {invalid.length + dups.length > 0
                      ? `${invalid.length + dups.length} row${invalid.length + dups.length !== 1 ? "s" : ""} skipped (invalid or duplicate).`
                      : "All rows are valid."}
                  </span>
                  <div className={block.elem("modal-actions").toClassName()}>
                    <Button size="small" look="outlined" onClick={() => setReviewOpen(false)} disabled={submitting}>
                      Back
                    </Button>
                    <Button size="small" onClick={submit} disabled={submitting} waiting={submitting}>
                      {submitting ? "Inviting…" : `Invite ${ready.length} member${ready.length !== 1 ? "s" : ""}`}
                    </Button>
                  </div>
                </div>
              }
            >
              <p className={block.elem("description").toClassName()}>
                These people will be added to <b>{org?.title ?? "the organisation"}</b> with the role shown. New
                accounts get a set-password email; existing accounts are added directly. Nothing is saved until you
                confirm.
              </p>
              <div className={block.elem("table-wrap").mod({ modal: true }).toClassName()}>
                <table className={block.elem("table").toClassName()}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Email</th>
                      <th>Name</th>
                      <th>Role</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ready.map((m, i) => (
                      <tr key={m.email}>
                        <td className={block.elem("muted").toClassName()}>{i + 1}</td>
                        <td>{m.email}</td>
                        <td>{[m.first_name, m.last_name].filter(Boolean).join(" ") || m.name || "—"}</td>
                        <td>
                          {ROLE_OPTIONS.find((r) => r.value === (m.role || defaultRole))?.label}
                          {!m.role && m.rawRole ? ` (was "${m.rawRole}")` : ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Modal>
          )}
        </>
      )}
    </div>
  );
};
